/** Line counts only. The rendered diff highlights words within those lines. */
export interface LineDiffCounts {
    addedLines: number;
    removedLines: number;
}

// An LCS table is O(n*m); past this a long prompt would visibly hang the panel.
const MAX_TABLE_CELLS = 4_000_000;

export function getLineDiffCounts(
    before: string,
    after: string,
): LineDiffCounts {
    const beforeLines = splitLines(before);
    const afterLines = splitLines(after);

    // Trim the matching head and tail so the table only covers what changed.
    let start = 0;
    while (
        start < beforeLines.length &&
        start < afterLines.length &&
        beforeLines[start] === afterLines[start]
    ) {
        start++;
    }

    let end = 0;
    while (
        end < beforeLines.length - start &&
        end < afterLines.length - start &&
        beforeLines[beforeLines.length - 1 - end] ===
            afterLines[afterLines.length - 1 - end]
    ) {
        end++;
    }

    const beforeMiddle = beforeLines.slice(start, beforeLines.length - end);
    const afterMiddle = afterLines.slice(start, afterLines.length - end);

    if (beforeMiddle.length === 0 || afterMiddle.length === 0) {
        return {
            addedLines: afterMiddle.length,
            removedLines: beforeMiddle.length,
        };
    }

    const common =
        beforeMiddle.length * afterMiddle.length > MAX_TABLE_CELLS
            ? countCommonLines(beforeMiddle, afterMiddle)
            : longestCommonSubsequence(beforeMiddle, afterMiddle);

    return {
        addedLines: afterMiddle.length - common,
        removedLines: beforeMiddle.length - common,
    };
}

function splitLines(value: string) {
    return value.length === 0 ? [] : value.split(/\r\n|\r|\n/);
}

function longestCommonSubsequence(before: string[], after: string[]) {
    let previous = new Array<number>(after.length + 1).fill(0);
    let current = new Array<number>(after.length + 1).fill(0);

    for (let i = 1; i <= before.length; i++) {
        for (let j = 1; j <= after.length; j++) {
            current[j] =
                before[i - 1] === after[j - 1]
                    ? previous[j - 1] + 1
                    : Math.max(previous[j], current[j - 1]);
        }
        [previous, current] = [current, previous];
    }

    return previous[after.length];
}

/** Fallback for very large inputs. Exact for pure inserts and deletes, slightly generous when lines move. */
function countCommonLines(before: string[], after: string[]) {
    const remaining = new Map<string, number>();
    for (const line of before) {
        remaining.set(line, (remaining.get(line) ?? 0) + 1);
    }

    let common = 0;
    for (const line of after) {
        const count = remaining.get(line) ?? 0;
        if (count > 0) {
            remaining.set(line, count - 1);
            common++;
        }
    }

    return common;
}
